/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError, GetJobNotificationsData, GetJobNotificationsResponse } from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { scheduleInvalidate } from '@/hooks/query/invalidationScheduler';
import { useSubscribeToEvents } from '@/hooks/useSubscribeToEvents';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

export type UseJobNotificationsQueryParams = {
  params?: Omit<GetJobNotificationsData, 'tenantId'>;
};

/**
 * Custom hook that fetches job notification counts.
 *
 * Features:
 * - Fetches counts of undismissed job notifications
 * - Automatically scopes to selected tenant and subtenant
 * - Type-safe API response handling
 *
 * @param options Configuration object:
 *   - params: Optional query parameters (subtenantId)
 *
 * @returns React Query result containing:
 *   - data: { totalWarnings: number, totalErrors: number, totalTimeouts: number, totalRunning: number }
 *   - isLoading: Boolean for initial load state
 *   - isFetching: Boolean for any loading state
 *   - isError: Boolean for error state
 *   - error: Error object if request failed
 *
 * Note: tenantId is automatically pulled from the management plane state store
 *
 * @example
 * ```tsx
 * const { data: notifications } = useJobNotificationsQuery();
 * console.log(notifications?.totalWarnings, notifications?.totalErrors);
 * ```
 */
const JOB_NOTIFICATION_EVENTS = [
  'node:operational-state-changed',
  'node-config:assigned',
  'node:update-baseline',
  'job:updated',
  'job:completed',
  'job:timedout',
  'subtenant-membership:updated',
];

export function useJobNotificationsQuery({ params }: UseJobNotificationsQueryParams = {}) {
  const selectedTenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);
  const selectedSubtenantId = useMgmtPlaneStateStore((state) => state.selectedSubtenantID);
  const queryClient = useQueryClient();

  const subtenantId = params?.subtenantId ?? selectedSubtenantId;
  const queryKey = ['jobNotifications', selectedTenantId, subtenantId];

  // Subscribe to job-related events to invalidate the query cache
  useSubscribeToEvents(JOB_NOTIFICATION_EVENTS, () => scheduleInvalidate(queryClient, queryKey));

  return useQuery<GetJobNotificationsResponse, ApiError>({
    queryKey,
    queryFn: () => {
      return mgmtPlaneAPIClient.jobs.getJobNotifications({
        tenantId: selectedTenantId!,
        subtenantId: subtenantId || undefined,
      });
    },
    enabled: !!selectedTenantId,
  });
}
